<template>
  <div class="access-control-page">
    <div class="columns">
      <div class="column col-12">
        <label class="form-switch">
          <input type="checkbox" v-model="enabled">
          <i class="form-icon"/> Habilitar controle de acesso por horário
        </label>
      </div>
    </div>

    <template v-if="enabled">
      <h6 class="card-title mt-2">{{ title || 'Controle de acesso ao sistema' }}</h6>
      <p class="text-gray" v-if="description">
        {{ description }}
      </p>

      <div class="columns mt-2">
        <div class="column col-12">
          <button
            class="btn btn-sm"
            @click="applyToWorkingDays"
          >
            Aplicar para todos os dias úteis
          </button>
        </div>
      </div>

      <div class="columns mt-2">
        <div class="column col-12">
          <table class="table table-striped">
            <thead>
              <tr>
                <th width="50">Ativo</th>
                <th>Dia da semana</th>
                <th>Horário inicial</th>
                <th>Horário final</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(day, index) in accessDays" :key="index">
                <td>
                  <label class="form-checkbox">
                    <input type="checkbox" v-model="day.enabled">
                    <i class="form-icon"></i>
                  </label>
                </td>
                <td>
                  <strong>{{ day.label }}</strong>
                </td>
                <td>
                  <div class="time-input-wrapper">
                    <input
                      type="text"
                      class="form-input"
                      :class="{'has-error': getDayError(day, 'startTime')}"
                      v-model="day.startTime"
                      :disabled="!day.enabled"
                      placeholder="08:00"
                      v-mask="'00:00'"
                    >
                    <template v-if="getDayError(day, 'startTime')">
                      <div class="form-input-hint form-input-hint-absolute">
                        {{ getDayError(day, 'startTime') }}
                      </div>
                    </template>
                  </div>
                </td>
                <td>
                  <div class="time-input-wrapper">
                    <input
                      type="text"
                      class="form-input"
                      :class="{'has-error': getDayError(day, 'endTime')}"
                      v-model="day.endTime"
                      :disabled="!day.enabled"
                      placeholder="18:00"
                      v-mask="'00:00'"
                    >
                    <template v-if="getDayError(day, 'endTime')">
                      <div class="form-input-hint form-input-hint-absolute">
                        {{ getDayError(day, 'endTime') }}
                      </div>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <div class="columns mt-2">
      <div class="column col-12">
        <button
          class="btn btn-primary"
          :class="{loading: saving}"
          :disabled="isSaveDisabled"
          @click="save"
        >
          {{ saveButtonText || 'Salvar controle de acesso' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'AccessControl',
  props: {
    entityId: {
      type: String,
      required: true,
    },
    endpoint: {
      type: String,
      default: '/professionals',
    },
    resourceKey: {
      type: String,
      default: 'professional',
    },
    title: {
      type: String,
      default: 'Controle de acesso ao sistema',
    },
    description: {
      type: String,
      default: 'Defina os dias da semana e horários em que o colaborador pode acessar o sistema.',
    },
    saveButtonText: {
      type: String,
      default: 'Salvar controle de acesso',
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      enabled: false,
      accessDays: [
        {
          day: 0,
          label: 'Domingo',
          enabled: false,
          startTime: '08:00',
          endTime: '18:00',
        },
        {
          day: 1,
          label: 'Segunda-feira',
          enabled: true,
          startTime: '08:00',
          endTime: '18:00',
        },
        {
          day: 2,
          label: 'Terça-feira',
          enabled: true,
          startTime: '08:00',
          endTime: '18:00',
        },
        {
          day: 3,
          label: 'Quarta-feira',
          enabled: true,
          startTime: '08:00',
          endTime: '18:00',
        },
        {
          day: 4,
          label: 'Quinta-feira',
          enabled: true,
          startTime: '08:00',
          endTime: '18:00',
        },
        {
          day: 5,
          label: 'Sexta-feira',
          enabled: true,
          startTime: '08:00',
          endTime: '18:00',
        },
        {
          day: 6,
          label: 'Sábado',
          enabled: false,
          startTime: '08:00',
          endTime: '12:00',
        },
      ],
    };
  },
  computed: {
    hasValidationErrors() {
      return this.accessDays.some((day) => {
        if (!day.enabled) return false;
        const hasStartTimeError = this.getDayError(day, 'startTime');
        const hasEndTimeError = this.getDayError(day, 'endTime');
        return hasStartTimeError || hasEndTimeError;
      });
    },
    hasMissingTimes() {
      return this.accessDays.some((day) => {
        if (!day.enabled) return false;
        const hasStartTime = day.startTime && day.startTime.length === 5;
        const hasEndTime = day.endTime && day.endTime.length === 5;
        return !hasStartTime || !hasEndTime;
      });
    },
    isSaveDisabled() {
      return this.saving
        || !this.entityId
        || this.hasValidationErrors
        || this.hasMissingTimes;
    },
    apiEndpoint() {
      return `${this.endpoint}/${this.entityId}`;
    },
    dayErrors() {
      const errors = {};
      this.accessDays.forEach((day) => {
        if (day.enabled && day.startTime && day.endTime
          && day.startTime.length === 5 && day.endTime.length === 5) {
          const start = moment(day.startTime, 'HH:mm');
          const end = moment(day.endTime, 'HH:mm');
          if (!end.isAfter(start)) {
            errors[`day_${day.day}_endTime`] = 'Horário final deve ser maior que o inicial';
          }
        }
      });
      return errors;
    },
  },
  watch: {
    entityId: {
      immediate: true,
      handler() {
        if (this.entityId) {
          this.load();
        }
      },
    },
  },
  methods: {
    getDayError(day, field) {
      const dayKey = `day_${day.day}_${field}`;
      return this.dayErrors[dayKey];
    },
    load() {
      if (!this.entityId) {
        return;
      }

      this.loading = true;
      const params = { user: true };
      this.$http.get(this.apiEndpoint, { params })
        .then(({ data }) => {
          if (!data) {
            return;
          }
          const resource = data.user || data[this.resourceKey];
          const accessControl = resource && resource.accessControl ? resource.accessControl : null;
          const accessEnabled = accessControl && typeof accessControl.enabled === 'boolean'
            ? accessControl.enabled
            : this.enabled;
          if (accessControl && typeof accessControl.enabled === 'boolean') {
            this.enabled = accessControl.enabled;
          }
          if (accessControl && Array.isArray(accessControl.schedule)) {
            const { schedule } = accessControl;
            this.accessDays = this.accessDays.map((defaultDay) => {
              const savedDay = schedule.find(item => item.dayOfWeek === defaultDay.day);
              if (!savedDay) {
                return {
                  ...defaultDay,
                  enabled: false,
                };
              }
              const ranges = Array.isArray(savedDay.ranges) ? savedDay.ranges : [];
              const firstRange = ranges.length > 0 ? ranges[0] : null;
              return {
                ...defaultDay,
                enabled: accessEnabled && ranges.length > 0,
                startTime: firstRange && firstRange.start ? firstRange.start : defaultDay.startTime,
                endTime: firstRange && firstRange.end ? firstRange.end : defaultDay.endTime,
              };
            });
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    applyToWorkingDays() {
      const workingDays = [1, 2, 3, 4, 5];
      const defaultStart = '08:00';
      const defaultEnd = '18:00';

      this.accessDays.forEach((day) => {
        if (workingDays.includes(day.day)) {
          day.enabled = true;
          day.startTime = defaultStart;
          day.endTime = defaultEnd;
        }
      });
    },
    save() {
      if (!this.entityId) {
        this.$toast.error('É necessário salvar o cadastro antes de configurar o controle de acesso');
        return;
      }

      if (this.hasValidationErrors) {
        this.$toast.error('Corrija os erros de validação antes de salvar');
        return;
      }

      if (this.hasMissingTimes) {
        this.$toast.error('Preencha todos os horários dos dias habilitados');
        return;
      }

      this.saving = true;

      const payload = {
        [this.resourceKey]: {
          accessControl: {
            enabled: this.enabled,
            schedule: this.accessDays.map(day => ({
              dayOfWeek: day.day,
              ranges: day.enabled ? [{
                start: day.startTime,
                end: day.endTime,
              }] : [],
            })),
          },
        },
      };

      this.$http.put(this.apiEndpoint, payload)
        .then(() => {
          this.$toast.show('Controle de acesso salvo com sucesso');
          this.$emit('saved', payload);
        })
        .catch((error) => {
          let errorMessage = 'Erro ao salvar controle de acesso';
          if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }
          this.$toast.error(errorMessage);
          this.$emit('error', error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss">
  @import "../../assets/scss/variables";
  .access-control-page {
    .card-title {
      margin-top: 16px;
    }
    .table {
      th {
        background-color: $bg-color;
      }
      td {
        vertical-align: middle;
      }
      .time-input-wrapper {
        position: relative;
        display: inline-block;
        max-width: 150px;
        width: 100%;

        input[type="text"] {
          max-width: 150px;
          width: 100%;
        }

        .form-input-hint-absolute {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 4px;
          z-index: 10;
          white-space: nowrap;
          background-color: rgba(255, 77, 77, 0.95);
          color: #fff;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

          &::before {
            content: '';
            position: absolute;
            bottom: 100%;
            left: 8px;
            border: 4px solid transparent;
            border-bottom-color: rgba(255, 77, 77, 0.95);
          }
        }
      }
    }
  }
</style>
